import { Box, Stack, Tooltip } from "@mui/material";
import {
  ActivityStatus,
  ActivityType,
  ExecutionPlanType,
  GetTierIiReportQuery,
} from "generated-graphql/graphql";
import { useActivityDependencies } from "hooks/useActivityDependencies";
import { CompleteActivity } from "./CompleteActivity";
import { Requeue } from "./Requeue";
import { SendToErrorTriage } from "./SendToErrorTriage";
import { SetToManual } from "./SetToManual";

type ActivityBottomToolbarProps = {
  activity: NonNullable<
    GetTierIiReportQuery["tierIIReport"]["currentWorkflow"]
  >["activities"][0];
  allActivities?: NonNullable<
    GetTierIiReportQuery["tierIIReport"]["currentWorkflow"]
  >["activities"];
};

export function ActivityBottomToolbar({
  activity,
  allActivities = [],
}: ActivityBottomToolbarProps) {
  // Check if activity is a staff activity and has its dependencies met
  const { canComplete, tooltipMessage } = useActivityDependencies(
    activity,
    allActivities
  );

  // Original logic: activity is "current" if not in these statuses
  const isCurrent = ![
    ActivityStatus.NotStarted,
    ActivityStatus.StaffCompleted,
    ActivityStatus.Completed,
  ].includes(activity?.status);

  // NEW: For staff activities, enable completion if:
  // 1. Activity is in a completable status (original logic), OR
  // 2. Activity is a staff activity with dependencies met (even if NotStarted)
  const isStaffActivity = activity?.type === ActivityType.Staff;
  const isErrorActivity = activity?.type === ActivityType.Error;
  const canCompleteOutOfOrder =
    isStaffActivity &&
    activity?.status !== ActivityStatus.Completed &&
    activity?.status !== ActivityStatus.StaffCompleted;

  const showCompleteButton =
    !isErrorActivity && (isCurrent || canCompleteOutOfOrder);
  const isCompleteButtonDisabled =
    isStaffActivity && !canComplete && !isCurrent;

  const canRequeue =
    isCurrent &&
    activity?.status === ActivityStatus.Error &&
    activity?.type !== ActivityType.Error;
  const skipResumeDialog = activity?.executionPlan === ExecutionPlanType.Human;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        justifyContent: "space-between",
        alignItems: "flex-start",
        "@media (max-width: 600px)": {
          flexDirection: "column",
          gap: 2,
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          flexWrap: "wrap",
          gap: 1,
          "@media (max-width: 600px)": {
            width: "100%",
            justifyContent: "center",
          },
        }}
      >
        <SetToManual activity={activity} />
        {canRequeue && (
          <Requeue activity={activity} showWarningPanel={!skipResumeDialog} />
        )}
        <SendToErrorTriage activity={activity} />
      </Stack>
      <Box
        sx={{
          "@media (max-width: 600px)": {
            width: "100%",
            display: "flex",
            justifyContent: "center",
          },
        }}
      >
        {showCompleteButton &&
          (() => {
            const completeButton = (
              <CompleteActivity
                activity={activity}
                showWarningPanel={
                  activity?.status === ActivityStatus.Error ||
                  activity.executionPlan === ExecutionPlanType.Automation
                }
                disabled={isCompleteButtonDisabled}
              />
            );
            return tooltipMessage ? (
              <Tooltip title={tooltipMessage} arrow>
                <span>{completeButton}</span>
              </Tooltip>
            ) : (
              completeButton
            );
          })()}
      </Box>
    </Box>
  );
}

export type ActivityButtonProps = {
  activity: ActivityBottomToolbarProps["activity"];
  refetchQueries?: string[];
};
